/**
 * Sorprendeme - Dynamic Backgrounds Helper
 * Maneja la selección aleatoria de backgrounds.
 *
 * Priority:
 *  1. `VITE_SORPRENDEME_BACKGROUNDS` / `_MOBILE` (comma-separated URLs)
 *  2. `BRAND_SORPRENDEME_BACKGROUND_URLS` from brandConfig (Ana defaults)
 *
 * SUPPORTED FORMATS:
 *  - Cloudflare Images, Cloudinary, Unsplash, or any public https URL
 *
 * Performance: memoized parsing; recommended ~1200px / q=75.
 */

import { BRAND_SORPRENDEME_BACKGROUND_URLS } from "@/lib/brandConfig";

const isDev = import.meta.env.DEV;

// Cache parsed URLs to avoid repeated parsing (performance optimization)
let cachedBackgrounds: string[] | null = null;
// Separate cache for mobile-optimized URLs (different aspect ratio / smaller size)
let cachedMobileBackgrounds: string[] | null = null;

/**
 * Mobile breakpoint — matches Tailwind's md (768px). Below this we prefer the
 * mobile background list if VITE_SORPRENDEME_BACKGROUNDS_MOBILE is set; above,
 * the standard list. The choice is made at first call and cached for the
 * session, so a phone rotation doesn't swap the image mid-flow.
 */
const MOBILE_BREAKPOINT_PX = 768;

function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
  } catch {
    return window.innerWidth <= MOBILE_BREAKPOINT_PX;
  }
}

/**
 * Parse background URLs from environment variable with caching.
 * Format: URLs separated by comma. Supports any public URL.
 *
 * Two env vars supported (use both for responsive backgrounds):
 *   VITE_SORPRENDEME_BACKGROUNDS         — desktop / fallback (REQUIRED)
 *   VITE_SORPRENDEME_BACKGROUNDS_MOBILE  — optional mobile-portrait variant
 *
 * If MOBILE is set and the viewport is ≤ 768px wide, parseBackgroundUrls()
 * returns the mobile list. Otherwise it returns the desktop list.
 */
function parseBackgroundUrls(forceMobile?: boolean): string[] {
  const wantsMobile = forceMobile ?? isMobileViewport();

  if (wantsMobile && cachedMobileBackgrounds !== null) {
    return cachedMobileBackgrounds;
  }
  if (!wantsMobile && cachedBackgrounds !== null) {
    return cachedBackgrounds;
  }

  const envValue = wantsMobile
    ? (import.meta.env.VITE_SORPRENDEME_BACKGROUNDS_MOBILE
        || import.meta.env.VITE_SORPRENDEME_BACKGROUNDS)
    : import.meta.env.VITE_SORPRENDEME_BACKGROUNDS;
  
  if (isDev) {
    console.log('[sorprende-backgrounds] Parsing backgrounds from env');
  }
  
  let urls: string[] = [];

  if (envValue && typeof envValue === "string" && envValue.trim().length > 0) {
    urls = envValue
      .split(",")
      .map((url) => url.trim())
      .filter(
        (url) =>
          url.length > 0 &&
          (url.startsWith("http://") || url.startsWith("https://")),
      );
  }

  // Brand defaults when Railway env is empty — never ship purple-only gradient
  // with a wrong-creator avatar on multi-brand forks.
  if (urls.length === 0) {
    urls = [...BRAND_SORPRENDEME_BACKGROUND_URLS];
    if (isDev) {
      console.log(
        `[sorprende-backgrounds] Using brandConfig defaults (${urls.length} URL(s))`,
      );
    }
  }

  if (urls.length === 0) {
    if (isDev) {
      console.warn(
        `[sorprende-backgrounds] ⚠️ No backgrounds (${wantsMobile ? "mobile" : "desktop"}) — gradient fallback`,
      );
    }
    if (wantsMobile) cachedMobileBackgrounds = [];
    else cachedBackgrounds = [];
    return [];
  }

  if (isDev) {
    console.log(
      `[sorprende-backgrounds] ✓ Loaded ${urls.length} ${wantsMobile ? "mobile" : "desktop"} background(s)`,
    );
  }

  if (wantsMobile) cachedMobileBackgrounds = urls;
  else cachedBackgrounds = urls;
  return urls;
}

/**
 * Get a random background URL from Railway environment variable
 * Uses session storage to persist the same background during user session
 * 
 * Returns:
 * - Image URL if VITE_SORPRENDEME_BACKGROUNDS is configured (use with url())
 * - null if not configured (component will use gradient directly)
 * 
 * IMPORTANT: VITE_SORPRENDEME_BACKGROUNDS MUST be set in Railway
 */
export function getRandomBackground(): string | null {
  // Get backgrounds from env var
  const backgrounds = parseBackgroundUrls();
  
  // If no backgrounds configured, return null (component will use gradient)
  if (backgrounds.length === 0) {
    if (isDev) {
      console.warn('[sorprende-backgrounds] No backgrounds configured - component will use gradient');
      console.warn('[sorprende-backgrounds] Configure VITE_SORPRENDEME_BACKGROUNDS in Railway');
    }
    return null;
  }
  
  // Select random background (rotates on each page load)
  const randomIndex = Math.floor(Math.random() * backgrounds.length);
  const selectedBg = backgrounds[randomIndex];
  
  if (isDev) {
    console.log('[sorprende-backgrounds] Selected background #' + (randomIndex + 1) + '/' + backgrounds.length + ':', selectedBg);
  }
  
  return selectedBg;
}

/**
 * Get all available backgrounds (for testing/preview).
 * @param forceMobile - if true, returns mobile-variant URLs even on desktop.
 */
export function getAllBackgrounds(forceMobile?: boolean): string[] {
  return parseBackgroundUrls(forceMobile);
}

/**
 * Clear session storage (no longer needed - backgrounds rotate on each visit)
 * @deprecated Session storage removed to enable background rotation
 */
export function clearBackgroundSession(): void {
  // No-op: session storage no longer used
}
